import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const expertSegments = [
  { label: '导入片段', desc: '开场3分钟的教学设计', icon: 'ri-play-circle-line', time: '00:00-03:00' },
  { label: '理论阐释片段', desc: '核心概念展开与论证', icon: 'ri-book-open-line', time: '08:20-18:40' },
  { label: '案例使用片段', desc: '案例选择与理论衔接', icon: 'ri-article-line', time: '18:40-25:10' },
  { label: '互动回应片段', desc: '提问、追问与学生回应', icon: 'ri-chat-smile-2-line', time: '25:10-32:30' },
  { label: '总结升华片段', desc: '价值引导与课堂收束', icon: 'ri-flag-line', time: '38:00-45:00' },
];

export default function P18EvidenceExpert({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            服务专家
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            证据<span className="text-primary-600">对专家</span>的价值
          </h2>
          <p className={`text-base text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            最大的价值是提效和稳尺，专家精力放在判断质量上
          </p>
        </div>

        {/* Before → After comparison */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-8 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          {/* Before */}
          <div className="p-6 rounded-2xl glass border-white/50">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-red-50/80 border border-red-200/40 flex items-center justify-center">
                <i className="ri-close-line text-sm text-red-400" />
              </div>
              <span className="text-sm font-body font-semibold text-foreground-700">传统方式</span>
            </div>
            <p className="text-sm text-foreground-500 font-body leading-relaxed">
              专家需要从头拖完整视频，一节45分钟的课，找片段就占用了大量精力，容易遗漏关键节点。
            </p>
            <div className="mt-4 p-3 rounded-xl bg-background-100/50">
              <div className="w-full h-2 rounded-full bg-background-300/50 mb-2 overflow-hidden">
                <div className="h-full w-full rounded-full bg-foreground-200/50" />
              </div>
              <p className="text-xs text-foreground-400 text-center font-mono">00:00 ———— 45:00 全程手动拖拽</p>
            </div>
          </div>

          {/* After */}
          <div className="p-6 rounded-2xl glass-liquid border-primary-200/30">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-accent-50/80 border border-accent-200/40 flex items-center justify-center">
                <i className="ri-check-line text-sm text-accent-600" />
              </div>
              <span className="text-sm font-body font-semibold text-foreground-700">AI辅助后</span>
            </div>
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              系统先定位关键片段，专家只需对片段进行专业判断。不同专家意见不一致时，回到同一组证据讨论。
            </p>
            <div className="mt-4 p-3 rounded-xl bg-white/60 border border-white/80">
              <div className="flex gap-1.5">
                {[0, 8, 18, 25, 32, 38].map((t, i) => (
                  <div key={t} className="flex-1 h-2 rounded-full relative overflow-hidden bg-background-200/50">
                    <div className={`absolute inset-0 rounded-full ${i === 0 ? 'bg-accent-300/60' : i === 3 ? 'bg-primary-300/60' : 'bg-background-300/40'}`} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-foreground-400 text-center font-mono mt-2">关键片段自动定位</p>
            </div>
          </div>
        </div>

        {/* Segment list */}
        <div className={`transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <h4 className="text-xs font-body font-semibold text-foreground-400 uppercase tracking-wider mb-4">系统预定位的关键片段</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {expertSegments.map((seg, i) => (
              <div
                key={seg.label}
                className="group p-4 rounded-2xl glass border-white/60 hover:border-primary-200/50 transition-all duration-300 hover:scale-[1.02]"
                style={{ transitionDelay: `${500 + i * 80}ms` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg glass-blue border-primary-200/40 flex items-center justify-center">
                    <i className={`${seg.icon} text-sm text-primary-600`} />
                  </div>
                  <span className="text-xs font-mono text-foreground-400">{seg.time}</span>
                </div>
                <p className="text-sm font-body font-semibold text-foreground-800">{seg.label}</p>
                <p className="text-xs text-foreground-500 font-body mt-1">{seg.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}